import React, { FC, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import Tooltip from "@natera/material/lib/tooltip";
import Svg from "@natera/material/lib/svg";
import { Utils } from "@natera/material/lib/date";
import classNames from "classnames";
import helpIcon from "@assets/svg/icons/help.svg";
import { useIsHoverViewport } from "@app/utils/useIsHoverViewport";
import { formatCancerType } from "./cancerTypeLabels";
import "./cancerTypeBlock.scss";

const messages = defineMessages({
  cancerTypeLabel: {
    id: "testCardCancerType",
    defaultMessage: "Cancer Type: {cancerType}",
  },
  moreInfo: {
    id: "testCardMoreInfo",
    defaultMessage: "More info",
  },
  procedureDate: {
    id: "testCardProcedureDate",
    defaultMessage: "Procedure date: {date}",
  },
  cancerTypeTooltip: {
    id: "cancerTypeTooltip",
    defaultMessage: "Cancer type: {cancerType}",
  },
});

export interface CancerTypeBlockProps {
  cancerType?: string;
  dateOfSurgery?: string;
  /**
   * When true, hides the visible "Cancer Type: X" label, centers the trigger
   * row, and puts BOTH the cancer type and procedure date inside the tooltip
   * on separate lines. Used on the Test Results Detail page header.
   */
  condensed?: boolean;
  /**
   * Card-only variant: render just the ❓ icon inline after the cancer-type
   * label, no visible "More info" text next to it. Used on TestCard where the
   * label + inline trigger need to fit on one line. Accessibility is
   * preserved via the trigger's `aria-label`.
   */
  iconOnly?: boolean;
}

const CancerTypeBlock: FC<CancerTypeBlockProps> = ({
  cancerType: rawCancerType,
  dateOfSurgery,
  condensed = false,
  iconOnly = false,
}) => {
  const intl = useIntl();
  const cancerType = formatCancerType(intl, rawCancerType);
  const isHoverViewport = useIsHoverViewport();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Below the hover breakpoint, close the tooltip when the user clicks
    // anywhere outside the trigger. Attached after the current click tick
    // so the trigger's own onClick doesn't immediately dismiss it.
    if (isHoverViewport || !open) return;
    const handleOutside = () => setOpen(false);
    const t = window.setTimeout(() => {
      document.addEventListener("click", handleOutside);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("click", handleOutside);
    };
  }, [open, isHoverViewport]);

  // Card variant: nothing to render if there's no cancer type at all.
  // Condensed variant: also require either cancerType or dateOfSurgery to
  // have something worth showing in the tooltip.
  const hasAnyData = !!cancerType || !!dateOfSurgery;
  if (condensed ? !hasAnyData : !cancerType) return null;

  // Trigger visibility rules:
  // - card variant: only when there's a dateOfSurgery to reveal.
  // - condensed variant: always show the trigger when we have any data.
  const showTrigger = condensed ? hasAnyData : !!dateOfSurgery;

  // Tooltip content:
  // - card variant: single line "Procedure date: {date}".
  // - condensed variant: two lines, cancer type first (with lowercase "type"
  //   per Figma), procedure date second.
  const tooltipContent = condensed ? (
    <>
      {cancerType && (
        <div>
          {intl.formatMessage(messages.cancerTypeTooltip, { cancerType })}
        </div>
      )}
      {dateOfSurgery && (
        <div>
          {intl.formatMessage(messages.procedureDate, {
            date: Utils.getUSLocaleDate(new Date(dateOfSurgery)),
          })}
        </div>
      )}
    </>
  ) : (
    dateOfSurgery &&
    intl.formatMessage(messages.procedureDate, {
      date: Utils.getUSLocaleDate(new Date(dateOfSurgery)),
    })
  );

  const trigger = (
    <div
      className={classNames("upp-cancer-type__more-info", {
        "upp-cancer-type__more-info--icon-only": iconOnly,
      })}
      role="button"
      tabIndex={0}
      aria-label={intl.formatMessage(messages.moreInfo)}
      aria-expanded={isHoverViewport ? undefined : open}
      onClick={
        isHoverViewport
          ? undefined
          : (e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen((o) => !o);
        }
      }}
    >
      {!iconOnly && <span>{intl.formatMessage(messages.moreInfo)}</span>}
      {/* Decorative — the parent has aria-label="More info". */}
      <Svg content={helpIcon} aria-hidden="true" />
    </div>
  );

  const tooltipTrigger = showTrigger && (
    <Tooltip
      className="upp-cancer-type-tooltip"
      content={tooltipContent}
      position={{ xPos: 1 }}
      opened={isHoverViewport ? undefined : open}
      onClose={() => setOpen(false)}
    >
      {trigger}
    </Tooltip>
  );

  // Icon-only card variant: emit the trigger INSIDE the label text so it
  // flows as inline content. Any other layout (block-sibling, flex-child)
  // pushes the icon to its own line when the label wraps, because the
  // label <div> already occupies its full flex row. See UPP-5683 screenshot
  // regression.
  if (iconOnly && !condensed && cancerType) {
    return (
      <div className="upp-cancer-type upp-cancer-type--icon-only">
        <span className="upp-cancer-type__label">
          {intl.formatMessage(messages.cancerTypeLabel, { cancerType })}{" "}
          {tooltipTrigger}
        </span>
      </div>
    );
  }

  return (
    <div
      className={classNames("upp-cancer-type", {
        "upp-cancer-type--condensed": condensed,
      })}
    >
      {!condensed && cancerType && (
        <div className="upp-cancer-type__label">
          {intl.formatMessage(messages.cancerTypeLabel, { cancerType })}
        </div>
      )}
      {tooltipTrigger}
    </div>
  );
};

export default CancerTypeBlock;
